<template>
  <el-row :gutter="0" class="h-full">
    <el-col :span="20" class="relative">
      <div v-loading="!isReadyToScan || isLoadingScanMO" class="scan-view">
        <div>
          <div id="scan-area">
            <div id="video-area">
              <video playsinline autoplay class="opacity-0 " id="video"></video>
            </div>
          </div>
          <canvas id="captureCanvas" v-show="false"></canvas>
        </div>
        <div
          class="absolute z-50 flex justify-center items-center right-3 left-3 mb-2 flex-row"
          v-if="isMultipleScan && showMultipleScan"
          style="bottom: calc(27%)"
        >
          <ScannedValueInput v-model:valueCode="valueCode" @update:completeScan="handleCompleteScan" />
        </div>

        <div
          v-if="showMultipleScan"
          class="absolute z-50 right-8 flex top flex-col justify-center items-center"
          style="bottom: calc(20%)"
        >
          <BulkScanSwitchButton @update:isMultipleScan="isMultipleScan = $event" :isMultipleScan="isMultipleScan" />
        </div>

        <div class="message-wrapper center ">
          <p class="text-message" v-html="$t('please_scan_barcode')"></p>
        </div>
      </div>
      <div v-if="manufacturingPlan" class="main-content-wrapper absolute inset-0 z-50">
        <div class="h-fit z-50 header-content-wrapper">
          <div class="flex items-center justify-between header-line">
            <div class="font-semibold">
              {{ $t('manufactured_order_number') }}
              <span class="font-bold">{{ manufacturingPlan?.orderNumber }}</span>
            </div>
            <div v-if="manufacturingPlan?.manufactureCompletionDate">
              <span>{{ $t('manufactured_completion_date') }}:</span>
              <span class="font-bold">{{
                formatLocalizedDate(new Date(manufacturingPlan.manufactureCompletionDate))
              }}</span>
            </div>
          </div>

          <div class="font-semibold header-line">
            <span class="font-bold mr-7">{{ totalPlannedQuantityDisplay }}</span>
            <span class="font-bold">{{ conversionFactorDisplay }}</span>
          </div>

          <div class="header-line">
            <span>{{ $t('item_code') }}</span
            ><span class="font-bold">({{ manufacturingPlan?.itemCode }})</span>
          </div>

          <div class="header-line">
            {{ manufacturingPlan?.itemName }}
            <span class="font-bold">
              {{ barcodeInfo?.serialSetting?.producedSerialMaximumTotalNumber }}{{ $t('piece') }}</span
            >
          </div>
        </div>
        <div class="mt-2 table-container">
          <div class="sticky-header">
            <el-table :data="[fakeRow]" :show-header="false" class="main-row-table">
              <el-table-column min-width="148" align="center">
                <template #default>
                  {{ $t('summary') }}
                </template>
              </el-table-column>

              <el-table-column min-width="250" align="center">
                <template #default> {{ currentTotalInitAmount }}({{ manufacturingPlan.stockUnit }})</template>
              </el-table-column>

              <el-table-column min-width="120" class-name="col-date" align="center">
                <template #default> {{ serialDataLocal?.length }} {{ $t('piece') }}/ </template>
              </el-table-column>

              <el-table-column min-width="205" align="center">
                <template #default>
                  {{ barcodeInfo?.serialSetting?.producedSerialMaximumTotalNumber }}{{ $t('piece') }}
                </template>
              </el-table-column>

              <el-table-column min-width="180" align="center">
                <template #default> {{ $t('trolley_number') }} </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="scrollable-content" ref="scrollableContent">
            <el-table
              v-if="combinedSerialData"
              :data="combinedSerialData"
              border
              :show-header="false"
              :row-class-name="rowClassName"
            >
              <el-table-column min-width="148" align="center">
                <template #default="{ row }">
                  <span v-if="row._skeletonId">···</span>
                  <span v-else>{{ getRowText(row) }}</span>
                </template>
              </el-table-column>

              <el-table-column prop="code" min-width="250" align="center" />

              <el-table-column prop="scannedDateTime" min-width="120" class-name="col-date" align="center">
                <template #default="{ row }">
                  {{ row.scannedDateTime ? formatDayMonth(new Date(row.scannedDateTime)) : '-' }}
                </template>
              </el-table-column>

              <el-table-column prop="scannedDateTime" min-width="205" align="center">
                <template #default="{ row }">
                  {{ row.scannedDateTime ? displayTime(new Date(row.scannedDateTime)) : '-' }}
                </template>
              </el-table-column>

              <el-table-column min-width="180" align="center">
                <template #default="{ row }">
                  <span v-if="row.code !== 'ERROR' && !row.isDeleted && !row.isDuplicated">
                    {{ row.trollyNumber || '-' }} - {{ getTrolleyDisplayIndex(row) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </el-col>
    <el-col :span="4" class="relative pt-10 side-panel">
      <div class="w-full">
        <div class="card-shadow text-black display-date mt-1">
          <p class="text-center">{{ displayDate }}</p>
        </div>
        <div class="card-shadow bg-black">
          <p class="px-4 text-center display-time">{{ displayTime(currentTime) }}</p>
        </div>
        <div class="mt-10">
          <div class="card-shadow">
            <div class="trolly-label text-center text-white p-2">{{ $t('trolley_count') }}</div>
            <div class="bg-black text-center trolly-content p-2">
              {{ currentTrolleyNumber }}/{{ maxTrolleyNumber }} {{ $t('trolley') }}
            </div>
          </div>
          <div class="card-shadow">
            <div class="trolly-label text-center text-white p-2">{{ $t('trolley_capacity') }}</div>
            <div class="bg-black text-center trolly-content p-2">{{ sequentialNumberDisplay }} 本</div>
          </div>
        </div>
      </div>

      <div class="p-4">
        <el-button :class="['btn-cancel-active', 'text-black']" @click="toggleScanMode">
          {{ scanMode === 'add' ? $t('scan_cancel') : $t('add') }}
        </el-button>
      </div>

      <div class="btn-complete">
        <el-button :class="['btn-complete-content', 'text-black']" @click="showCompleteDialog">
          <div>{{ $t('item') }}</div>
          <div>{{ $t('complete') }}</div>
        </el-button>
      </div>
    </el-col>
  </el-row>

  <!-- Complete Confirmation Dialog -->
  <div class="complete-dialog-area">
    <el-dialog
      v-model="isShowCompleteDialog"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="complete-dialog"
    >
      <div class="complete-confirmation-dialog">
        <div class="dialog-header">
          <div class="header-text">{{ $t('item_switch_quantity_confirmation') }}</div>
        </div>
        <div class="dialog-body">
          <p class="mismatch-text">{{ $t('order_quantity_mismatch') }}</p>
          <p class="mismatch-text">{{ $t('adjust_quantity_question') }}</p>
          <div class="quantity-info">
            <button class="dialog-btn finish-btn" @click="handleSubmitTracking" :disabled="loading">
              {{ loading ? $t('processing') : $t('complete') }}
            </button>
            <div class="quantity-row-wrapper">
              <div class="quantity-row">
                <span class="quantity-label">{{ $t('order_quantity_label') }}</span>
                <span class="quantity-value">{{ adjustedPlannedQuantity }} 個</span>
              </div>
              <div class="quantity-row">
                <span class="quantity-label">{{ $t('read_quantity_label') }}</span>
                <span class="quantity-value">{{ scannedQuantity }} 個</span>
              </div>
            </div>
            <button class="dialog-btn finish-btn" @click="handleRescan" :disabled="loading">{{ $t('rescan') }}</button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>

  <!-- Error Dialog -->
  <ErrorDialog v-model="isShowErrorDialog" />

  <WorkCenterErrorDialog v-model="isOpenWorkCenterError" :errorMessage="workCenterErrorMessage" />

  <ConfirmDialog
    v-model="showManufacturingDateDialog"
    :message="$t('manufacturing_date_not_today')"
    :confirmText="$t('OK')"
    :cancelText="$t('cancel')"
    @confirm="onManufacturingDateDialogConfirm"
    @cancel="onManufacturingDateDialogCancel"
  />

  <!-- Delete Confirmation Dialog -->
  <div class="delete-confirm-dialog-area">
    <el-dialog
      v-model="isShowDeleteConfirmDialog"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="delete-confirm-dialog"
    >
      <div class="delete-confirmation-content">
        <div class="delete-dialog-header">
          <div class="delete-header-text">{{ $t('confirm_delete_title') }}</div>
        </div>
        <div class="delete-dialog-body">
          <p class="delete-question">{{ $t('confirm_delete_question') }}</p>
          <div v-if="pendingRemoveCodes.length" class="delete-items-list">
            <div v-for="(code, index) in pendingRemoveCodes" :key="index" class="delete-item">
              {{ code }}
            </div>
          </div>
        </div>
        <div class="delete-dialog-buttons">
          <button class="delete-dialog-btn confirm-delete-btn" @click="onConfirmDelete">
            {{ $t('delete') }}
          </button>
          <button class="delete-dialog-btn cancel-delete-btn" @click="onCancelDelete">
            {{ $t('cancel') }}
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { updateBarcodeTrackingData } from '@/apis/barcodeAPI'
import { ISerialHistoryFullDataItem } from '@/apis/serialAPI'
import BulkScanSwitchButton from '@/components/common/BulkScanSwitchButton.vue'
import ScannedValueInput from '@/components/common/ScannedValueInput.vue'
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue'
import ErrorDialog from '@/components/dialog/ErrorDialog.vue'
import WorkCenterErrorDialog from '@/components/dialog/WorkCenterErrorDialog.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import ManufacturingDateCheckMixin from '@/components/mixins/ManufacturingDateCheckMixin.vue'
import SoundMixin from '@/components/mixins/SoundMixin.vue'
import {
  BARCODE_ACTION_COMPLETE,
  CHECK_SERIAL_DETAIL,
  CHECK_SERIAL_LAST_HISTORY_DETAIL,
  FETCH_BARCODE,
  GET_SERIAL_HISTORIES,
  GET_TRACKING,
  OPEN_DIALOG,
  SAVE_SCANNED_WORK_CENTER_CODE,
  UPDATE_SCANNED_RESULT,
} from '@/store/actions'
import { AsyncScanQueue } from '@/utils/asyncQueue'
import {
  ALLOWED_SERIAL_PREFIX,
  DATE_TIME,
  getLocalStorageKey,
  getSerialErrorMessage,
  LEFT_AMOUNT_START_TROLLEY_INDEX,
  LOCAL_STORAGE_ITEM,
  QRCODE_BARCODE_TYPE_REGEX_NEW,
  trollyNumberLimitBT,
  trollyNumberLimitTP,
  UNIT_OPTIONS,
} from '@/utils/constants'
import { EBCRType, ECustomFieldType, EOperatorType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { logError, queueError } from '@/utils/errorLogger'
import { openMessage, promptVideoPermission, trollyNumberLimit } from '@/utils/helpers'
import { scannerBridge } from '@/utils/scannerBridge'
import { IListStringItem, TError } from '@/utils/types/commonTypes'
import { ICustomFieldData } from '@/utils/types/fieldData'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { AxiosError } from 'axios'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { mixins, Options } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

@Options({
  components: {
    BulkScanSwitchButton,
    ScannedValueInput,
    ConfirmDialog,
    ErrorDialog,
    WorkCenterErrorDialog,
  },
  name: 'ScanViewOperator',
})
export default class ScanViewOperator extends mixins(BarcodeReaderMixin, ManufacturingDateCheckMixin, SoundMixin) {
  _barcodeDataCapture: BarcodeCapture | undefined
  codeReader = new BrowserMultiFormatReader()
  isReadyToScan = false
  isLoadingScanMO = false
  isHandlingAddScan = false
  video: HTMLVideoElement | null = null
  listBlob: Blob[] = []
  isShowBarcodeDialog = false
  isShowCompleteDialog = false
  currentTime: Date = new Date()
  serialDataLocal: IListStringItem[] = []
  displaySerialData: (IListStringItem & { _skeletonId?: string })[] = []
  skeletonCounter = 0
  fakeRow = {}
  scanMode: 'add' | 'remove' = 'add'
  lotNumber = ''
  loading = false
  isShowErrorDialog = false
  // duplicateErrorVisible = false
  duplicateErrorMessage = ''
  isPreventSaveLocal = false
  showExistingDataErrorDialog = false
  pendingRemoveCodes: string[] = []
  adjustedPlannedQuantity = 0
  isShowDeleteConfirmDialog = false
  private timeIntervalId: ReturnType<typeof setInterval> | null = null
  private adjustQuantityIntervalId: ReturnType<typeof setInterval> | null = null
  workCenterErrorMessage = ''
  isOpenWorkCenterError = false
  private scanQueue: AsyncScanQueue<string> | null = null

  scanUnsubscribe: (() => void) | null = null
  statusUnsubscribe: (() => void) | null = null

  unmounted() {
    if (this.scanQueue) this.scanQueue.destroy()
    if (this.timeIntervalId) {
      clearInterval(this.timeIntervalId)
      this.timeIntervalId = null
    }
    if (this.adjustQuantityIntervalId) {
      clearInterval(this.adjustQuantityIntervalId)
      this.adjustQuantityIntervalId = null
    }
    if (this.scanUnsubscribe) {
      this.scanUnsubscribe()
    }
    if (this.statusUnsubscribe) {
      this.statusUnsubscribe()
    }
  }

  @Watch('serialDataLocal', { deep: true })
  onUpdateSerialDataLocal() {
    if (!this.isPreventSaveLocal && this.project && this.barcodeInfo && this.keyTrackingDataDraft) {
      localStorage.setItem(
        this.keyTrackingDataDraft,
        JSON.stringify({
          machine: this.machineId,
          sirial: this.serialDataLocal,
        })
      )
    }
  }

  @Watch('displaySerialData', { deep: true })
  onUpdateDisplaySerialData() {
    if (!this.isPreventSaveLocal && this.project && this.barcodeInfo && this.keyTrackingDataDraftForDisplay) {
      localStorage.setItem(
        this.keyTrackingDataDraftForDisplay,
        JSON.stringify({
          machine: this.machineId,
          display: this.displaySerialData,
        })
      )
    }
    // Auto-scroll to bottom when data changes
    this.$nextTick(() => {
      this.scrollToBottom()
    })
  }

  private createNewSerialItem(item: Partial<IListStringItem>): IListStringItem {
    const { trolleyNumber, trolleyIndex } = this.handleGetTrollyInfo()
    return {
      id: item.id,
      conversionUnit: item.conversionUnit,
      itemCode: item.itemCode ?? '',
      itemName: item.itemName,
      productId: item.productId,
      code: item.code || '',
      limitAmount: item.limitAmount ?? this.defaultLimitAmount,
      amount: item.initAmount ?? this.defaultAmount,
      currentAmount: item.currentAmount ?? this.defaultAmount,
      unit: item.unit ?? this.defaultUnit,
      scannedDateTime: item.scannedDateTime ?? new Date().toISOString(),
      initAmount: item.initAmount,
      warnings: item.warnings ?? [],
      trollyNumber: trolleyNumber,
      trollyIndex: trolleyIndex,
      ...(item.isDeleted !== undefined && { isDeleted: item.isDeleted }),
      ...(item.isDuplicated !== undefined && { isDuplicated: item.isDuplicated }),
    }
  }

  private scrollToBottom() {
    const scrollableContent = this.$refs.scrollableContent as HTMLElement
    if (scrollableContent) {
      scrollableContent.scrollTop = scrollableContent.scrollHeight
    }
  }

  get unitOptions() {
    return UNIT_OPTIONS.map((unit) => ({ label: unit, value: unit }))
  }

  get currentTotalInitAmount(): number {
    return this.serialDataLocal.reduce((total, item) => total + (item.amount || 0), 0)
  }

  get defaultUnit(): string {
    return this.unitOptions[0]?.value || ''
  }

  get defaultAmount(): number {
    return 0
  }

  get defaultLimitAmount(): number {
    return 10
  }

  get producedLastTrollyIndex(): number {
    return this.barcodeInfo?.serialSetting.producedLastTrollyIndex
  }

  get producedLastTrollyNumber(): number {
    return this.barcodeInfo?.serialSetting.producedLastTrollyNumber
  }

  get currentItemCount(): number {
    return this.serialDataLocal?.length
  }

  get maxItemCount(): number {
    return this.barcodeInfo?.serialSetting?.producedSerialMaximumTotalNumber || 0
  }

  get conversionFactor(): number {
    return this.barcodeInfo?.serialSetting?.conversionFactor
  }

  get totalPlannedQuantityStockUnit(): number {
    return this.manufacturingPlan?.totalPlannedQuantityStockUnit || 0
  }

  get totalPlannedQuantityDisplay() {
    const total = this.totalPlannedQuantityStockUnit
    const unit = this.manufacturingPlan?.stockUnit || ''

    return `${total}${unit}`
  }

  get conversionFactorDisplay(): string {
    const total = this.totalPlannedQuantityStockUnit
    const unit = this.manufacturingPlan?.stockUnit || ''
    const factor = this.conversionFactor || 0
    const totalSerial = this.serialDataLocal.length

    if (!factor || totalSerial === 0) {
      return `${total}${unit}`
    }

    const remainder = total - totalSerial * factor

    let display = `${this.$t('bottle_capacity_label')}  ${factor}${unit} x ${totalSerial}`

    if (remainder > 0) {
      display += ` + ${remainder.toFixed(2)}${unit}`
    }

    return display
  }

  get producedSerialTotalNumber(): number {
    return this.barcodeInfo?.serialSetting.producedSerialTotalNumber || 0
  }

  get currentTrolleyNumber(): number {
    if (this.serialDataLocal?.length === 0) return 1
    const lastItem = this.serialDataLocal[this.serialDataLocal.length - 1]
    return lastItem?.trollyNumber || 1
  }

  get maxTrolleyNumber(): number {
    if (!this.maxItemCount) return 1
    return Math.ceil(this.maxItemCount / trollyNumberLimit(this.serialDataLocal))
  }

  calculateProcessedTrolleyIndex(lastItem: IListStringItem, currentTrolleyIndex: number): number {
    if (lastItem.code.startsWith('TP')) {
      return currentTrolleyIndex % trollyNumberLimitTP || trollyNumberLimitTP
    } else if (lastItem.code.startsWith('BT')) {
      return currentTrolleyIndex % trollyNumberLimitBT || trollyNumberLimitBT
    } else {
      return currentTrolleyIndex % trollyNumberLimit(this.serialDataLocal) || trollyNumberLimit(this.serialDataLocal)
    }
  }

  get sequentialNumberDisplay(): string {
    if (this.serialDataLocal?.length === 0) {
      return `0/${trollyNumberLimit(this.serialDataLocal)}`
    }
    const lastItem = this.serialDataLocal[this.serialDataLocal.length - 1]
    const currentTrolleyIndex = lastItem?.trollyIndex || 0

    const processedTrolleyIndex = this.calculateProcessedTrolleyIndex(lastItem, currentTrolleyIndex)

    return `${processedTrolleyIndex}/${trollyNumberLimit(this.serialDataLocal)}`
  }

  get displayDate() {
    return format(new Date(), DATE_TIME.DISPLAY_FORMAT)
  }

  displayTime(time: Date) {
    return format(time, DATE_TIME.TIME_WITH_SECOND_FORMAT)
  }

  get barcodeInfo() {
    return this.$store.state.barcode?.barcode
  }

  get manufacturingPlan() {
    return this.$store.getters.manufacturingPlan
  }

  get previousTrackingData() {
    return this.$store.getters.trackingData
  }

  get project() {
    return this.$store.state.project.details
  }

  get keyTrackingDataDraft(): string {
    if (!this.project || !this.barcodeInfo) return ''
    return getLocalStorageKey.trackingDataDraft(this.project.code, this.barcodeInfo.id, '1')
  }

  get keyTrackingDataDraftForDisplay(): string {
    if (!this.project || !this.barcodeInfo) return ''
    return getLocalStorageKey.trackingDataDraft(this.project.code, this.barcodeInfo.id, '1.display')
  }

  get keyPreviousTrackingDataDraft(): string {
    if (!this.project || !this.barcodeInfo) return ''

    return getLocalStorageKey.trackingDataDraft(this.project.code, this.barcodeInfo.id, '1.previous')
  }

  get machineId(): string {
    return (this.$route.params.machine as string) || ''
  }

  get scannedQuantity(): number {
    return this.serialDataLocal.length
    // return this.displaySerialData?.filter((item) => !item.isDeleted).length || 0
  }

  get combinedSerialData(): IListStringItem[] {
    return this.displaySerialData.sort((a, b) => {
      const dateA = new Date(a.scannedDateTime || 0).getTime()
      const dateB = new Date(b.scannedDateTime || 0).getTime()
      return dateA - dateB
    })
  }

  getRowText(row: IListStringItem): string {
    if (row.code === 'ERROR') return this.$t('error')
    if (row.isDeleted) return this.$t('scan_cancel')
    if (row.isDuplicated) return this.$t('duplicate')
    return this.$t('location.inStock')
  }

  getTrolleyDisplayIndex(row: IListStringItem): string {
    if (!row.trollyIndex) return '-'

    let limit: number
    if (row.code.startsWith('TP')) {
      limit = trollyNumberLimitTP
    } else if (row.code.startsWith('BT')) {
      limit = trollyNumberLimitBT
    } else {
      return String(row.trollyIndex)
    }

    const displayIndex = ((row.trollyIndex - 1) % limit) + 1
    return String(displayIndex)
  }

  async defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.isReadyToScan = true
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    await this.handleScanResult((temp.text as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    await this.handleScanResult((result[0].rawValue as string) || '')
    if (!this.isMultipleScan) {
      this.vidOff()
    }
  }

  async scanditDataResult(symbology: Barcode) {
    this.isReadyToScan = true
    if (!symbology) return
    await this.handleScanResult(symbology.data || '')
  }

  get isScanningBlocked() {
    return false
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  async mounted() {
    this.scanQueue = new AsyncScanQueue<string>(
      async (value) => {
        await this.handleOperationScan(value)
      },
      { delayMs: 0 }
    )
    this.timeIntervalId = setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
    this.currentTime = new Date()
    this.showMultipleScan =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.isEnable ?? false

    // Check if barcodeId is provided in route params
    const barcodeId = this.$route.params.barcodeId as string
    if (barcodeId) {
      await this.fetchBarcodeAndSwitchMode(barcodeId)
    }

    if (this.adjustedPlannedQuantity === 0 && this.barcodeInfo?.serialSetting?.producedSerialMaximumTotalNumber) {
      this.adjustedPlannedQuantity = this.barcodeInfo?.serialSetting?.producedSerialMaximumTotalNumber
    }

    if (this.machineId) {
      this.$store.dispatch(SAVE_SCANNED_WORK_CENTER_CODE, this.machineId)
    }

    if (localStorage.getItem('allowedCam')) {
      await this.initCameraSelection()
      await this.setupCameraSetting()
    } else {
      this.isReadyToScan = true
    }

    this.scanUnsubscribe = scannerBridge.onScan(async (data: string) => {
      console.log('Scanner Data table:', data)

      const scannedValue = data.startsWith('true') ? data.substring(4) : data
      await this.handleScanResult(scannedValue.trim())
    })

    // this.statusUnsubscribe = scannerBridge.onStatusChange((status: ScannerStatus) => {
    //   console.log('Scanner Status: 1', status)
    // })
  }

  async setupCameraSetting() {
    try {
      this.isUserAutheticated()
      this.isReadyToScan = false
      switch (this.barcodeReaderType as EBCRType) {
        case EBCRType.SCANDIT:
          await this.initScanditReader()
          break
        case EBCRType.BARCODE_DETECTION:
          await this.initBarcodeReaderPolyFill()
          break
        default:
          await promptVideoPermission()
          await this.initZxingReader()
          break
      }
    } catch (error) {
      logError(error as Error, { method: 'setupCameraSetting', barcodeReaderType: this.barcodeReaderType })
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  resetScannedValue() {
    this.listBlob = []
    this.valueCode = ''
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  redirectScanMoPage() {
    this.$router.replace({
      name: 'scan-tablet',
      params: { machine: this.machineId },
    })
  }

  isUserAutheticated() {
    const isRestrictedPage = this.$route.meta.restricted
    if (!this.isAuth && isRestrictedPage) {
      this.redirectScanMoPage()
    }
  }

  formatLocalizedDate(date: Date) {
    const format = this.$t('date_format')
    return dayjs(date).format(format)
  }

  formatDayMonth(date: Date) {
    return format(date, DATE_TIME.MONTH_DAY)
  }

  async handleCompleteScan() {
    if (!this.isMultipleScan || this.valueCode.trim() === '') {
      return
    }
    this.processScannedText(this.valueCode)
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }

    const videoConstraints = this.selectedCamera
      ? { aspectRatio: 1, deviceId: { exact: this.selectedCamera } }
      : { facingMode: 'environment', aspectRatio: 1 }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints })
      await this.setStream(stream)
    } catch (e) {
      const error = e as Error & { name?: string }
      logError(error, { method: 'setupVideo', errorName: error.name, selectedCamera: this.selectedCamera })
      errorHandler(error.name === 'NotAllowedError' ? 'camera_access_not_allow' : error)
    }
  }

  async snapshot() {
    try {
      const tempCanvas = document.querySelector('#captureCanvas') as HTMLCanvasElement
      const video = document.querySelector('video')
      if (!video || !tempCanvas) return
      tempCanvas.width = video.videoWidth
      tempCanvas.height = video.videoHeight
      const context = tempCanvas.getContext('2d')
      if (!context) return
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const blob = await new Promise((resolve) => tempCanvas.toBlob(resolve))
      return blob
    } catch (error) {
      logError(error as Error, { method: 'snapshot' })
      console.error('Error during snapshot:', error)
    }
  }

  handleSaveScannedInfo(blob: Blob) {
    this.listBlob.push(blob)
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  async handleScanResult(plainText: string) {
    if (this.isScanningBlocked) return
    if (this.scanQueue) this.scanQueue.enqueue(plainText)
  }

  private addSkeletonItem(plainText: string): string {
    const skeletonId = `skeleton-${++this.skeletonCounter}`
    const skeletonItem: IListStringItem & { _skeletonId: string } = {
      ...this.createNewSerialItem({ code: plainText, scannedDateTime: new Date().toISOString() }),
      _skeletonId: skeletonId,
    }
    this.displaySerialData.push(skeletonItem)
    return skeletonId
  }

  private removeSkeletonItem(skeletonId: string) {
    const skeletonIdx = this.displaySerialData.findIndex(
      (item) => (item as IListStringItem & { _skeletonId?: string })._skeletonId === skeletonId
    )
    if (skeletonIdx !== -1) {
      this.displaySerialData.splice(skeletonIdx, 1)
    }
  }

  async handleOperationScan(plainText: string) {
    const trimmedText = plainText.trim()
    const skeletonId = this.addSkeletonItem(trimmedText)

    try {
      if (this.scanMode === 'add') {
        await this.handleScanAdd(trimmedText)
      } else {
        this.handleScanRemove(trimmedText)
      }
    } finally {
      this.removeSkeletonItem(skeletonId)
    }
  }

  private async handleScanAdd(scannedValue: string) {
    this.isHandlingAddScan = true
    if (!this.validateScanAdd(scannedValue)) {
      this.playFailSound()
      return
    }

    if (!(await this.validateSerialCode(scannedValue, EOperatorType.ADD))) {
      this.playFailSound()
      return
    }
    this.playSuccessSound()
  }

  private validateScanAdd(scannedValue: string): boolean {
    // if (!this.validateMaxLength()) {
    //   return false
    // }

    if (!this.validateEmptyInput(scannedValue)) {
      return false
    }

    if (!this.validateAndShowFalseInput(scannedValue)) {
      return false
    }

    if (!this.validateAndShowPrefixError(scannedValue)) {
      return false
    }

    if (!this.validateAndShowDuplicateError(scannedValue)) {
      return false
    }

    return true
  }

  private createErrorItem(originalCode?: string): IListStringItem {
    return this.createNewSerialItem({
      code: originalCode || 'ERROR',
      scannedDateTime: new Date().toISOString(),
      isDeleted: true,
    })
  }

  private createDuplicateErrorItem(code: string): IListStringItem {
    return this.createNewSerialItem({
      code: code,
      scannedDateTime: new Date().toISOString(),
      isDeleted: false,
      isDuplicated: true,
    })
  }

  private validateAndShowFalseInput(scannedValue: string): boolean {
    if (!scannedValue || scannedValue.toLowerCase().startsWith('false')) {
      this.isShowErrorDialog = true
      this.isHandlingAddScan = false
      this.displaySerialData.push(this.createErrorItem())
      return false
    }

    return true
  }

  private validateEmptyInput(scannedValue: string): boolean {
    if (scannedValue === '') {
      this.isHandlingAddScan = false
      this.displaySerialData.push(this.createDuplicateErrorItem(scannedValue))
      return false
    }

    return true
  }

  private validateAndShowPrefixError(scannedValue: string): boolean {
    if (!this.validateCodePrefix(scannedValue)) {
      if (this.isShowErrorDialog) {
        this.isHandlingAddScan = false
        return false
      }

      this.isShowErrorDialog = true
      this.displaySerialData.push(this.createErrorItem())

      const titleMessage = this.$t('invalid_code_prefix')
      queueError(titleMessage + scannedValue, { method: 'validateAndShowPrefixError', scannedValue })
      this.isHandlingAddScan = false
      return false
    }

    return true
  }

  private validateAndShowDuplicateError(scannedValue: string): boolean {
    const existingItemIndex = this.serialDataLocal.findIndex((item) => item.code === scannedValue)

    if (existingItemIndex !== -1) {
      const existingItem = this.serialDataLocal[existingItemIndex]
      const sequentialNumber = existingItemIndex + 1
      const trolleyNumber = existingItem.trollyNumber || 1

      this.displaySerialData.push(this.createDuplicateErrorItem(existingItem.code))

      this.duplicateErrorMessage = this.$t('duplicate_bottle_message', { trolleyNumber, sequentialNumber })
      // this.duplicateErrorVisible = true
      queueError(this.duplicateErrorMessage + scannedValue, {
        method: 'validateAndShowDuplicateError',
        scannedValue,
        trolleyNumber,
        sequentialNumber,
      })
      this.isHandlingAddScan = false
      return false
    }

    return true
  }

  private validateCodePrefix(code: string): boolean {
    const activeItems = this.displaySerialData.filter((item) => !item.isDeleted && !item.isDuplicated)
    if (activeItems.length === 0) {
      return code.startsWith(ALLOWED_SERIAL_PREFIX.TP) || code.startsWith(ALLOWED_SERIAL_PREFIX.BT)
    }

    const firstCodePrefix = activeItems[0]?.code?.substring(0, 2)
    const newCodePrefix = code.substring(0, 2)

    return firstCodePrefix === newCodePrefix
  }

  private handleScanRemove(scannedValue: string) {
    const index = this.serialDataLocal.findIndex((item) => item.code === scannedValue)
    if (index === -1) {
      openMessage(this.$t('item_not_found'), 'warning')
      return
    }

    // Check if code is already in pending list
    if (this.pendingRemoveCodes.includes(scannedValue)) {
      openMessage(this.$t('item_already_in_remove_list'), 'warning')
      return
    }

    this.pendingRemoveCodes.unshift(scannedValue)
  }

  private onConfirmDelete() {
    this.scanMode = 'add'
    if (this.pendingRemoveCodes.length === 0) {
      this.isShowDeleteConfirmDialog = false
      return
    }

    // Remove all pending codes from serialDataLocal
    this.pendingRemoveCodes.forEach((code) => {
      const index = this.serialDataLocal.findIndex((item) => item.code === code)
      if (index !== -1) {
        this.serialDataLocal.splice(index, 1)
      }
    })

    // Update trolley indexes and numbers after removal
    this.updateTrolleyIndexes()

    // Add removed items to display with isDeleted flag
    this.pendingRemoveCodes.forEach((code) => {
      const displayRemovedItem = this.createNewSerialItem({
        code: code,
        scannedDateTime: new Date().toISOString(),
        isDeleted: true,
      })
      this.displaySerialData.push(displayRemovedItem)
    })

    openMessage(this.$t('items_deleted', { count: this.pendingRemoveCodes.length }), 'success')
    this.pendingRemoveCodes = []
    this.isShowDeleteConfirmDialog = false
  }

  private onCancelDelete() {
    this.pendingRemoveCodes = []
    this.isShowDeleteConfirmDialog = false
    this.scanMode = 'add'
  }

  private updateTrolleyIndexes() {
    this.serialDataLocal.forEach((item, index) => {
      const newTrolleyIndex = index + 1
      item.trollyIndex = newTrolleyIndex
      item.trollyNumber = Math.ceil(newTrolleyIndex / trollyNumberLimit(this.serialDataLocal))
    })
  }

  async fetchBarcodeAndSwitchMode(barcodeId: string) {
    this.isLoadingScanMO = true
    try {
      await this.$store.dispatch(FETCH_BARCODE, {
        id: barcodeId,
      })
      if (this.checkManufacturingDate()) {
        return
      }
      this.lotNumber = this.generateLotNumberFromDate(this.manufacturingPlan?.manufactureCompletionDate || '')
      await this.loadSavedTrackingData()
    } catch (error) {
      logError(error as Error, {
        method: 'fetchBarcodeAndSwitchMode',
        barcodeId,
        rawError: error instanceof Error ? undefined : this.safeStringify(error),
      })
      errorHandler(error as TError)
    } finally {
      this.isLoadingScanMO = false
    }
  }

  async processScannedText(text: string) {
    const valCode = text.trim()
    const regexBarcodeId = QRCODE_BARCODE_TYPE_REGEX_NEW
    const urlParts = valCode.split('/')
    if (valCode.match(regexBarcodeId)) {
      const barcodeId = urlParts.pop()
      this.isLoadingScanMO = true
      try {
        await this.$store.dispatch(FETCH_BARCODE, {
          id: barcodeId,
        })
        this.lotNumber = this.generateLotNumberFromDate(this.manufacturingPlan?.manufactureCompletionDate || '')
        this.isShowBarcodeDialog = true
      } catch (error) {
        logError(error as Error, {
          method: 'processScannedText',
          scannedText: valCode,
          barcodeId,
          rawError: error instanceof Error ? undefined : this.safeStringify(error),
        })
        errorHandler(error as TError)
      } finally {
        this.isLoadingScanMO = false
      }
    }
  }

  private safeStringify(value: unknown): string {
    try {
      return JSON.stringify(value)
    } catch (e) {
      return String(value)
    }
  }

  private isTimeoutError(error: unknown): boolean {
    if (!error) return false
    const axiosError = error as AxiosError
    return axiosError?.code === 'ECONNABORTED' || (error as Error)?.message?.includes('timeout')
  }

  private handleTimeoutError(error: unknown, code: string) {
    this.displaySerialData.push(this.createErrorItem())

    logError(error as Error, { method: 'validateSerialCode', reason: 'timeout', serialCode: code })
    errorHandler(new Error(this.$t('request_timeout')))
  }

  private handleExpiredProductError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
      return false
    }

    const errorObj = error as Record<string, string[]>
    const errorKey = Object.keys(errorObj)[0]
    const errorCodes = errorObj[errorKey]

    if (!errorCodes) {
      return false
    }

    const errorCode = errorCodes.find((code) => getSerialErrorMessage(code, errorKey))
    if (!errorCode) {
      return false
    }

    this.$store.dispatch(OPEN_DIALOG, {
      title: this.$t('el.messagebox.error'),
      message: getSerialErrorMessage(errorCode, errorKey),
    })

    return true
  }

  private async handleAmountInsufficientError(code: string): Promise<boolean> {
    try {
      const {
        serialHistories,
      }: { serialHistories: ISerialHistoryFullDataItem[] } = await this.$store.dispatch(GET_SERIAL_HISTORIES, { code })
      const currentManufacturingPlanId = this.manufacturingPlan?.id
      const useHistories = (serialHistories || []).filter((history) => history.operatorType === EOperatorType.USE)

      if (useHistories.some((history) => history.manufacturingPlanId === currentManufacturingPlanId)) {
        const duplicatedSerialError = this.$t('serial_already_registered_as_tracking_data')
        openMessage(duplicatedSerialError, 'warning')
        logError(duplicatedSerialError, {
          method: 'handleAmountInsufficientError',
          serialCode: code,
          manufacturingPlanId: currentManufacturingPlanId,
          reason: 'serial_already_registered_in_current_order',
        })
        return true
      }

      if (useHistories.some((history) => history.manufacturingPlanId !== currentManufacturingPlanId)) {
        const usedInAnotherOrderError = this.$t('serial_used_in_another_order')
        openMessage(usedInAnotherOrderError, 'warning')
        logError(usedInAnotherOrderError, {
          method: 'handleAmountInsufficientError',
          serialCode: code,
          manufacturingPlanId: currentManufacturingPlanId,
          reason: 'serial_used_in_another_order',
        })
        return true
      }
    } catch (error) {
      logError(error as Error, { method: 'handleAmountInsufficientError', serialCode: code })
      errorHandler(error as TError)
      return true
    }

    return false
  }

  private async handleSerialUsageError(code: string): Promise<boolean> {
    try {
      const lastHistoryDetail = await this.$store.dispatch(CHECK_SERIAL_LAST_HISTORY_DETAIL, { code })
      const { itemName, itemCode } = this.manufacturingPlan
      const { itemName: serialItemName, itemCode: serialItemCode } = lastHistoryDetail

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemCode}</div>
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${serialItemCode}</div>
          <div>${serialItemName}</div>
          <div>(${code})</div>
          <div>${this.$t('serial_already_input_error')}</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      logError(error as Error, { method: 'handleSerialUsageError', serialCode: code })
      errorHandler(error as TError)
    }
    return false
  }

  private async handleExpirationError(code: string): Promise<boolean> {
    try {
      const lastHistoryDetail = await this.$store.dispatch(CHECK_SERIAL_LAST_HISTORY_DETAIL, { code })
      const { itemName, itemCode } = this.manufacturingPlan
      const { itemName: serialItemName, itemCode: serialItemCode, expiredDateTime } = lastHistoryDetail

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemCode}</div>
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${this.$t('qr_expired_cannot_use_for_manufacturing')}</div>
          <div style="margin-top: 20px;">${serialItemCode} : ${serialItemName}</div>
          <div style="margin-top: 20px;">${this.$t('expiration_date')} : ${format(
        new Date(expiredDateTime),
        DATE_TIME.DISPLAY_FORMAT
      )} </div>
          <div style="margin-top: 20px;">(${code})</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      logError(error as Error, { method: 'handleExpirationError', serialCode: code })
      errorHandler(error as TError)
    }
    return false
  }

  private async validateSerialCode(code: string, operatorType: EOperatorType): Promise<boolean> {
    try {
      this.loading = true

      const data: IListStringItem = await this.$store.dispatch(CHECK_SERIAL_DETAIL, {
        operatorType: operatorType,
        code,
        isEdit: !!this.previousTrackingData,
      })

      const newItem = this.createNewSerialItem(data)
      if (operatorType === EOperatorType.ADD) {
        newItem.id = 0
      }
      if (newItem.warnings && newItem.warnings.length > 0) {
        const messages = newItem.warnings.map((warning) => `[${warning}]: ${this.$t(`errors.${warning}`)}`)
        const message = messages.join('<br>')
        openMessage(message, 'warning', 5000, {
          dangerouslyUseHTMLString: true,
        })
        logError(message, { method: 'validateSerialCode', serialCode: code, warnings: newItem.warnings })
      }
      this.serialDataLocal.push(newItem)
      this.displaySerialData.push(newItem)
      return true
    } catch (error) {
      if (error === '3106' && (await this.handleAmountInsufficientError(code))) {
        this.displaySerialData.push(this.createErrorItem(code))
        return false
      }

      if (error === '3114') {
        await this.handleSerialUsageError(code)
        this.displaySerialData.push(this.createErrorItem(code))
        logError(`Serial usage prohibited (3114): ${code}`, {
          method: 'validateSerialCode',
          serialCode: code,
          errorCode: '3114',
          operatorType,
        })
        return false
      }

      if (error === '3123') {
        await this.handleExpirationError(code)
        this.displaySerialData.push(this.createErrorItem(code))
        logError(`Serial expired (3123): ${code}`, {
          method: 'validateSerialCode',
          serialCode: code,
          errorCode: '3123',
          operatorType,
        })
        return false
      }

      if (this.handleExpiredProductError(error)) {
        this.displaySerialData.push(this.createErrorItem(code))
        logError(`Expired product error: ${code}`, {
          method: 'validateSerialCode',
          serialCode: code,
          operatorType,
          rawError: this.safeStringify(error),
        })
        return false
      }

      if (this.isTimeoutError(error)) {
        this.handleTimeoutError(error, code)
      } else {
        this.displaySerialData.push(this.createErrorItem(code))
        logError(error as Error, {
          method: 'validateSerialCode',
          serialCode: code,
          operatorType,
          isEdit: !!this.previousTrackingData,
          rawError: error instanceof Error ? undefined : this.safeStringify(error),
        })
        errorHandler(error as TError)
      }
      return false
    } finally {
      this.loading = false
      this.isHandlingAddScan = false
    }
  }

  async handleCheckPreviousTrackingData() {
    try {
      if (!this.barcodeInfo?.id) {
        return false
      }
      const trackingData = await this.$store.dispatch(GET_TRACKING, {
        barcodeId: this.barcodeInfo.id,
        nextTrackPointKey: '1',
      })

      if (trackingData && trackingData.customFields) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  formatPreviousTrackingData(rawData: Record<string, unknown>): Record<string, unknown> {
    const formattedData: Record<string, unknown> = {}
    Object.entries(rawData).forEach(([key, value]) => {
      const fieldData = value as ICustomFieldData

      switch (fieldData.fieldType) {
        case ECustomFieldType.LIST_STRING: {
          const listString = fieldData.listString ? [...fieldData.listString] : []
          formattedData[key] = listString.map((item) => this.loadItem(item))
          break
        }
        case ECustomFieldType.TEXT:
        case ECustomFieldType.EMAIL:
        case ECustomFieldType.NUMBER: {
          formattedData[key] = value
          break
        }
        default:
          formattedData[key] = value
      }
    })

    return formattedData
  }

  loadItem(item: IListStringItem): IListStringItem {
    return {
      id: item.id,
      conversionUnit: item.conversionUnit,
      itemCode: item.itemCode ?? '',
      itemName: item.itemName,
      productId: item.productId,
      code: item.code || '',
      limitAmount: item.limitAmount,
      amount: item.currentAmount,
      currentAmount: item.currentAmount,
      unit: item.unit,
      scannedDateTime: item.scannedDateTime ? new Date(item.scannedDateTime).toISOString() : new Date().toISOString(),
      initAmount: item.initAmount,
      warnings: item.warnings ?? [],
      trollyNumber: item.trollyNumber,
      trollyIndex: item.trollyIndex,
    }
  }

  mergeLocalStorageWithApiData(trackingDataFromApi: Record<string, unknown>): Record<string, unknown> {
    const trackingDataFromLocal = localStorage.getItem(this.keyTrackingDataDraft)
    if (trackingDataFromLocal) {
      try {
        const parsedLocalData = JSON.parse(trackingDataFromLocal)

        if (parsedLocalData.sirial && Array.isArray(parsedLocalData.sirial)) {
          const apiSerial = (trackingDataFromApi.sirial as IListStringItem[]) || []
          const localSerial = parsedLocalData.sirial as IListStringItem[]

          const { mergedSerial } = this.mergeSerialData(apiSerial, localSerial)
          trackingDataFromApi.sirial = mergedSerial

          return trackingDataFromApi
        }
      } catch (error) {
        logError(error as Error, { method: 'mergeLocalStorageWithApiData', storageKey: this.keyTrackingDataDraft })
        console.error('Error parsing localStorage data:', error)
      }
    }
    return trackingDataFromApi
  }

  mergeSerialData(apiSerial: IListStringItem[], localSerial: IListStringItem[]) {
    const apiCodes = new Set(apiSerial.map((item) => item.code))
    const duplicateCodes: string[] = []

    const merged = [...apiSerial]
    for (const localItem of localSerial) {
      if (apiCodes.has(localItem.code)) {
        duplicateCodes.push(localItem.code)
      } else {
        merged.push(localItem)
      }
    }
    merged.sort((a, b) => {
      const dateA = new Date(a.scannedDateTime || 0).getTime()
      const dateB = new Date(b.scannedDateTime || 0).getTime()
      return dateA - dateB
    })
    const operatorType = this.barcodeInfo?.serialSetting?.operatorType
    const limit = trollyNumberLimit(merged)
    merged.forEach((item, index) => {
      const newIndex =
        operatorType === EOperatorType.CORRECT_VALUE ? LEFT_AMOUNT_START_TROLLEY_INDEX + index : index + 1
      const newTrolleyNumber = operatorType === EOperatorType.CORRECT_VALUE ? 0 : Math.ceil(newIndex / limit)
      item.trollyIndex = newIndex
      item.trollyNumber = newTrolleyNumber
    })

    return { mergedSerial: merged, duplicateCodes }
  }

  handleGetPreviousTrackingData() {
    const trackingData = this.$store.getters.trackingData
    if (trackingData && trackingData.customFields && this.barcodeInfo) {
      const trackingDataFromApi = this.formatPreviousTrackingData(trackingData.customFields)

      const listStringApiSerial = this.processListStringSerial(trackingDataFromApi.sirial as IListStringItem[])
      const listStringPrevious = localStorage.getItem(this.keyPreviousTrackingDataDraft)
      if (listStringPrevious && listStringApiSerial.join(', ') === listStringPrevious) {
        const trackingDataFromLocal = localStorage.getItem(this.keyTrackingDataDraft)
        if (trackingDataFromLocal) {
          const parsedLocalData = JSON.parse(trackingDataFromLocal)
          this.serialDataLocal = parsedLocalData.sirial || []
        } else {
          this.serialDataLocal = (trackingDataFromApi.sirial as IListStringItem[]) || []
        }
      } else {
        const mergedData = this.mergeLocalStorageWithApiData(trackingDataFromApi)
        this.serialDataLocal = (mergedData.sirial as IListStringItem[]) || []
      }

      // Load and merge displaySerialData from localStorage
      const displayFromLocalStorage = localStorage.getItem(this.keyTrackingDataDraftForDisplay)
      if (displayFromLocalStorage) {
        try {
          const parsedDisplay = JSON.parse(displayFromLocalStorage)
          if (parsedDisplay && parsedDisplay.display) {
            this.displaySerialData = parsedDisplay.display || []
          }
        } catch (error) {
          logError(error as Error, {
            method: 'handleGetPreviousTrackingData',
            storageKey: this.keyTrackingDataDraftForDisplay,
          })
          this.displaySerialData = JSON.parse(JSON.stringify(this.serialDataLocal))
        }
      } else {
        this.displaySerialData = JSON.parse(JSON.stringify(this.serialDataLocal))
      }

      if (this.keyTrackingDataDraft) {
        localStorage.setItem(
          this.keyTrackingDataDraft,
          JSON.stringify({
            machine: this.machineId,
            sirial: this.serialDataLocal,
          })
        )
      }
      this.savePreviousTrackingData(trackingData.customFields.sirial.listString as IListStringItem[])
    }
  }

  savePreviousTrackingData(listSerial: IListStringItem[]) {
    const codes = this.processListStringSerial(listSerial)
    if (this.keyPreviousTrackingDataDraft) {
      localStorage.setItem(this.keyPreviousTrackingDataDraft, codes.join(', '))
    }
  }

  processListStringSerial(listSerial: IListStringItem[]): string[] {
    if (listSerial && Array.isArray(listSerial)) {
      return listSerial.map((item) => item.code).sort()
    }
    return []
  }

  handleGetDataFromLocalStorage() {
    const valueFromLocalStorage = localStorage.getItem(this.keyTrackingDataDraft)
    const displayFromLocalStorage = localStorage.getItem(this.keyTrackingDataDraftForDisplay)

    if (!valueFromLocalStorage) {
      return
    }

    const parsedValue = JSON.parse(valueFromLocalStorage)
    if (valueFromLocalStorage && parsedValue && parsedValue.sirial) {
      this.isPreventSaveLocal = true

      // Check if machine ID has changed and update if needed
      if (parsedValue.machine !== this.machineId) {
        parsedValue.machine = this.machineId
        localStorage.setItem(
          this.keyTrackingDataDraft,
          JSON.stringify({
            machine: this.machineId,
            sirial: parsedValue.sirial,
          })
        )
      }

      this.serialDataLocal = parsedValue.sirial || []
    }

    if (displayFromLocalStorage) {
      try {
        const parsedDisplay = JSON.parse(displayFromLocalStorage)
        if (parsedDisplay && parsedDisplay.display) {
          this.displaySerialData = parsedDisplay.display || []
        }
      } catch (error) {
        logError(error as Error, {
          method: 'handleGetDataFromLocalStorage',
          storageKey: this.keyTrackingDataDraftForDisplay,
        })
        console.error('Error parsing display data from localStorage:', error)
        this.displaySerialData = JSON.parse(JSON.stringify(this.serialDataLocal))
      }
    }
  }

  async loadSavedTrackingData() {
    this.loading = true
    try {
      const isContainsPreviousData = await this.handleCheckPreviousTrackingData()
      if (isContainsPreviousData) {
        this.handleGetPreviousTrackingData()
        this.isPreventSaveLocal = false
      } else {
        this.handleGetDataFromLocalStorage()
        this.isPreventSaveLocal = false
      }
      // Initialize displaySerialData from serialDataLocal if not already loaded from localStorage
      if (!this.displaySerialData || this.displaySerialData.length === 0) {
        this.displaySerialData = JSON.parse(JSON.stringify(this.serialDataLocal))
      }
    } finally {
      this.loading = false
    }
  }

  async handleMergeCurrentSerialWithNewestData() {
    this.loading = true
    try {
      const isContainsPreviousData = await this.handleCheckPreviousTrackingData()
      if (isContainsPreviousData) {
        this.handleGetPreviousTrackingData()
        return
      }
    } finally {
      this.loading = false
    }
  }

  confirmMergeCurrentSerialWithNewestData() {
    this.showExistingDataErrorDialog = false
    this.handleMergeCurrentSerialWithNewestData()
  }

  cleanTrackingDataDraft() {
    Object.keys(localStorage).forEach((key) => {
      if (
        key === this.keyTrackingDataDraft ||
        key === this.keyPreviousTrackingDataDraft ||
        key === this.keyTrackingDataDraftForDisplay
      ) {
        localStorage.removeItem(key)
      }
    })
  }

  handleGetTrollyInfo() {
    if (this.serialDataLocal.length === 0) {
      return {
        trolleyNumber: this.producedLastTrollyNumber ?? 0,
        trolleyIndex: this.producedLastTrollyIndex,
      }
    }

    const lastItem = this.serialDataLocal[this.serialDataLocal.length - 1]
    const currentTrolleyIndex = lastItem.trollyIndex || this.producedLastTrollyIndex || 1

    const newTrolleyIndex = currentTrolleyIndex + 1
    const newTrolleyNumber = Math.ceil(newTrolleyIndex / trollyNumberLimit(this.serialDataLocal))

    return {
      trolleyNumber: newTrolleyNumber,
      trolleyIndex: newTrolleyIndex,
    }
  }

  generateLotNumberFromDate(dateString: string): string {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date
      .getFullYear()
      .toString()
      .slice(2) // Get last 2 digits of year
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  toggleScanMode() {
    this.scanMode = this.scanMode === 'add' ? 'remove' : 'add'
    if (this.scanMode === 'remove') {
      this.isShowDeleteConfirmDialog = true
    }
  }

  showCompleteDialog() {
    this.isShowCompleteDialog = true
  }

  adjustQuantity(amount: number) {
    this.adjustedPlannedQuantity = this.adjustedPlannedQuantity + amount
    if (this.adjustedPlannedQuantity < 0) {
      this.adjustedPlannedQuantity = 0
    }
  }

  startAdjustQuantity(amount: number) {
    this.adjustQuantity(amount)

    const repeatDelay = 500
    const repeatSpeed = 100

    const startAutoRepeat = () => {
      this.adjustQuantityIntervalId = setInterval(() => {
        this.adjustQuantity(amount)
      }, repeatSpeed)
    }

    this.adjustQuantityIntervalId = setTimeout(() => {
      startAutoRepeat()
    }, repeatDelay)
  }

  stopAdjustQuantity() {
    if (this.adjustQuantityIntervalId) {
      clearInterval(this.adjustQuantityIntervalId)
      clearTimeout(this.adjustQuantityIntervalId)
      this.adjustQuantityIntervalId = null
    }
  }

  handleRescan() {
    this.isShowCompleteDialog = false
  }

  scannedWorkCenterCode() {
    return localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE)
  }

  get currentManufacturingWRKC() {
    return this.barcodeInfo?.manufacturingPlan?.workCenterCode || ''
  }

  private validateWorkCenterCode(): boolean {
    if (!this.scannedWorkCenterCode()?.includes(this.currentManufacturingWRKC)) {
      this.workCenterErrorMessage = 'work_center_code_mismatch'
      logError(this.$t('work_center_code_mismatch'), {
        method: 'validateWorkCenterCode',
        scannedWorkCenterCode: this.scannedWorkCenterCode(),
        expectedWorkCenterCode: this.currentManufacturingWRKC,
        barcodeId: this.barcodeInfo?.id,
      })
      this.isOpenWorkCenterError = true
      return false
    }
    return true
  }

  private validateSubmit(): boolean {
    // if (this.adjustedPlannedQuantity < this.scannedQuantity) {
    //   openMessage(this.$t('please_reduce_plan_quantity'), 'warning')
    //   return false
    // }

    if (!this.validateWorkCenterCode()) {
      return false
    }

    return true
  }

  private prepareTrackingRequest() {
    if (!this.barcodeInfo?.id) {
      throw new Error('Barcode ID is required')
    }

    const deletedSerials = this.displaySerialData.filter(
      (item) =>
        item.isDeleted &&
        item.code !== 'ERROR' &&
        !item.isDuplicated &&
        !this.serialDataLocal.some((active) => active.code === item.code)
    )
    const serialDataWithRemoved = this.serialDataLocal.map((item) => ({
      ...item,
    }))

    return {
      barcodeIds: [this.barcodeInfo.id],
      trackingData: {
        // currentTrackPointKey: this.barcodeInfo.currentTrackPointKey || '0',
        currentTrackPointKey: '0',
        nextTrackPointKey: '1',
        customFields: {
          machine: {
            fieldType: ECustomFieldType.TEXT,
            text: this.machineId,
          },
          quantityOrderUnit: {
            fieldType: ECustomFieldType.NUMBER,
            number: this.adjustedPlannedQuantity,
          },
          sirial: {
            fieldType: ECustomFieldType.LIST_STRING,
            listString: serialDataWithRemoved,
            removedListString: deletedSerials.length > 0 ? deletedSerials : undefined,
          },
        },
      },
      selectedNextTrackpoint: '',
      isSkip: false,
      isDryRun: false,
    }
  }

  private handleTrackingSuccess() {
    this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'tracked')
    this.cleanTrackingDataDraft()
    this.isShowCompleteDialog = false
    openMessage(this.$t('tracking_completed_successfully'), 'success')
    this.redirectScanMoPage()
  }

  async handleSubmitTracking() {
    try {
      if (!this.validateSubmit()) {
        return
      }

      this.loading = true
      const request = this.prepareTrackingRequest()
      await updateBarcodeTrackingData(request)
      this.handleTrackingSuccess()
    } catch (error) {
      logError(error as Error, {
        method: 'handleSubmitTracking',
        barcodeId: this.barcodeInfo?.id,
        machineId: this.machineId,
        serialCount: this.serialDataLocal.length,
        quantityOrderUnit: this.adjustedPlannedQuantity,
        rawError: error instanceof Error ? undefined : this.safeStringify(error),
      })
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  handleConfirmComplete() {
    this.isShowCompleteDialog = false
    this.cleanTrackingDataDraft()
    this.redirectScanMoPage()
  }

  async handleCloseDialog() {
    this.isShowBarcodeDialog = false
    this.lotNumber = ''
  }

  rowClassName({ row }: { row: IListStringItem & { _skeletonId?: string } }) {
    if (row._skeletonId) return 'row-skeleton'
    if (row.isDuplicated) return 'row-duplicated'
    if (row.isDeleted || row.code === 'ERROR') return 'row-deleted'
    return 'row-normal'
  }
}
</script>

<style lang="scss">
@import './src/assets/css/mixins.scss';

.scan-view {
  position: relative;
  height: 100vh;
  background: rgb(32, 32, 32);
}

.scan-view video {
  height: 100vh;
}

#video-area {
  position: relative;

  #video {
    pointer-events: none !important;
  }
}

.main-row-table tr {
  background-color: #0000ff !important;
  color: white;
  font-size: 40px;
  font-weight: bold;
  line-height: 1.2 !important;
}

.main-row-table .el-table__body tr:hover > td {
  background-color: #0000ff !important;
  color: #ffffff;
}

.main-row-table td {
  border-color: #000 !important;
  padding: 12px 0 !important;
}

.main-row-table .cell {
  line-height: 1.2 !important;
  padding: 0 5px !important;
}

.col-date .cell {
  padding: 0 5px !important;
}

.main-row-table .el-table__row td:last-child .cell {
  white-space: nowrap;
}

.card-shadow {
  padding: 5px;
  margin: 0px 10px;
  font-size: 20px !important;
}

.btn-cancel-active,
.btn-cancel-active:focus,
.btn-cancel-active:hover {
  background-color: #ceff00;
  font-size: 40px;
  font-weight: bold;
  color: black;
  width: 100%;
  border-radius: 0px !important;
  border-top: 2px solid white;
  border-left: 2px solid white;
  border-right: 2px solid black;
  border-bottom: 2px solid black;
  opacity: 0.7;
}

.row-deleted {
  color: red !important;
}

.row-skeleton td {
  color: #888 !important;
  animation: skeleton-pulse 1s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.row-duplicated {
  color: orange !important;
}

.row-normal {
  color: white !important;
}

.btn-complete {
  bottom: 10px;
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
}

.btn-complete-content {
  background-color: #ceff00;
  font-size: 40px;
  font-weight: bold;
  width: 90%;
  border-radius: 0px !important;
  border-top: 2px solid white;
  border-left: 2px solid white;
  border-right: 2px solid black;
  border-bottom: 2px solid black;
  color: black;
}

.btn-complete-content:focus,
.btn-complete-content:hover {
  background-color: #ceff00;
  color: black;
  border-top: 2px solid white;
  border-left: 2px solid white;
  border-right: 2px solid black;
  border-bottom: 2px solid black;
}

.cell-custom {
  border: 1px solid #ddd;
  text-align: center;
  padding: 10px 20px;
}

.barcode-dialog-area {
  .el-dialog {
    padding: 0;
  }

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }

  .manufacturing-dialog {
    background: linear-gradient(135deg, #ffffff 0%, #8e8e8e 100%);
    border: 3px solid #000;
    padding: 20px 30px;
    min-width: 400px;
    font-family: sans-serif;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .manufacturing-dialog-content {
    width: 85%;
  }

  .dialog-field {
    display: flex;
    margin-bottom: 8px;
    font-size: 16px;
    line-height: 1.8;
    justify-content: center;
    align-items: center;
  }

  .field-label {
    color: #000;
    font-weight: normal;
    font-size: 25px;
  }

  .field-separator {
    margin: 0 4px;
    font-size: 25px;
  }

  .field-value {
    color: #000;
    font-size: 25px;
    font-weight: normal;
  }

  .lot-number-section {
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .lot-label {
    color: #000;
  }

  .lot-input-container {
    background: white;
  }

  .lot-input {
    border: 1px solid #000;
    outline: none;
    padding: 0px 10px;
    font-size: 25px;
    background: white;
    font-family: sans-serif;
    width: 35%;
  }

  .action-buttons {
    display: flex;
    gap: 30px;
    margin-top: 25px;
    justify-content: space-around;
  }

  .action-btn {
    min-width: 100px;
    padding: 10px 30px;
    font-size: 40px;
    font-weight: bold;
    border: 1px solid #000;
    background: white;
    cursor: pointer;
    font-family: sans-serif;
  }
}

.bottom-block-wraper {
  &.top {
    bottom: calc(20%);
  }

  &.bottom {
    bottom: calc(15%);
  }
}

.complete-dialog-area {
  .el-dialog {
    margin-top: 0px !important;
    padding: 0;
    max-width: 1000px !important;
    width: 1000px !important;
  }

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }

  .complete-confirmation-dialog {
    border: 5px solid #000;
    font-family: sans-serif;
    background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  }

  .dialog-header {
    background: #0066cc;
    padding: 15px;
  }

  .header-text {
    background: #ffffff;
    color: #000;
    font-size: 48px;
    font-weight: bold;
    text-align: center;
    padding: 15px;
    border: 2px solid #000;
  }

  .dialog-body {
    text-align: center;
    padding: 8px 20px 40px 20px;
  }

  .mismatch-text {
    font-size: 36px;
    font-weight: bold;
    color: #000;
    margin: 10px 0;
    line-height: 1.5;
  }

  .quantity-info {
    text-align: left;
    display: inline-block;
    display: flex;
    justify-content: space-between;
  }

  .quantity-row {
    display: flex;
    align-items: center;
    margin: 15px 0;
    font-size: 32px;
  }

  .quantity-label {
    font-weight: normal;
    color: #000;
    min-width: 200px;
  }

  .quantity-value {
    font-weight: bold;
    color: #000;
    margin-left: 20px;
  }

  .dialog-btn {
    background: #d3d3d3;
    min-width: 225px;
    min-height: 100px;
    padding: 20px 40px;
    font-size: 48px;
    font-weight: bold;
    cursor: pointer;
    font-family: sans-serif;
    border-top: 2px solid white;
    border-left: 2px solid white;
    border-right: 2px solid black;
    border-bottom: 2px solid black;
  }

  .quantity-row-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .adjust-btn {
    color: #000;
    font-size: 120px;
    padding: 0px;
  }

  .finish-btn {
    color: red;
    grid-column: 2;
    grid-row: 1;
  }

  .dialog-buttons {
    margin: 0px 20px 20px 20px;
    display: flex;
    justify-content: space-between;
  }

  .dialog-btn:hover {
    opacity: 0.9;
  }

  .dialog-btn:active {
    transform: translateY(2px);
  }
}

.error-dialog-area {
  .el-dialog {
    margin-top: 0px !important;
    padding: 0;
    border-radius: 0;
    max-width: 1000px !important;
    width: 1000px !important;
  }

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }

  .error-dialog-content {
    background: #ff0000;
    font-family: sans-serif;
    position: relative;
  }

  .error-header {
    padding: 20px;
  }

  .error-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .error-badge {
    background: #ffff00;
    color: #ff0000;
    font-size: 48px;
    font-weight: bold;
    padding: 15px 80px;
    box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  .error-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 40px 80px;
    gap: 60px;
  }

  .error-icon {
    flex-shrink: 0;
  }

  .error-circle {
    width: 310px;
    height: 310px;
    border-radius: 50%;
    background: linear-gradient(145deg, #cc0000 0%, #990000 100%);
    border: 15px solid #c0c0c0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), inset 0 -5px 15px rgba(0, 0, 0, 0.3);
    position: relative;
  }

  .error-x {
    color: #c0c0c0;
    font-size: 180px;
    font-weight: bold;
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.5), -2px -2px 4px rgba(255, 255, 255, 0.1);
    line-height: 1;
    font-family: Arial, sans-serif;
  }

  .error-message {
    text-align: left;

    p {
      color: #ffffff;
      font-size: 64px;
      font-weight: bold;
      margin: 0;
      line-height: 1.3;
      text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    }
  }
}

.message-wrapper {
  color: $pure-white;
  position: absolute;
  text-align: center;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;
  font-size: 50px;

  &.center {
    top: 40%;
  }
}

.text-message {
  font-size: 40px !important;
  font-weight: bold;
}

.display-date {
  background: #ffff00;
}

.display-time {
  color: #ffff00;
}

.side-panel {
  padding-top: 14px;
  padding-bottom: 1rem;
  background: #bdd608;
}

.header-content-wrapper {
  font-size: 30px;
  background: #009aff;
  color: #ffffff;
  height: 160px;
  padding: 8px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  border-bottom: 2px solid #000;
}

.header-line {
  line-height: 1.2;
}

.main-content-wrapper {
  background: #bdd608;
  padding: 14px 0px 0px 14px;
}

.table-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 205px);
  overflow: hidden;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #bdd608;
  border-bottom: 2px solid #000;
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  background: #000;
}

.scrollable-content .el-table {
  background: #000 !important;
  border: none !important;
}

.scrollable-content .el-table tr,
.scrollable-content .el-table td {
  background: #000 !important;
  font-size: 40px !important;
  border: none !important;
  padding: 0px !important;
}

.scrollable-content .cell {
  line-height: normal !important;
}

.scrollable-content .el-table__body tr:hover > td {
  background-color: #000 !important;
}

.scrollable-content .el-table td.el-table__cell {
  border: none !important;
}

.scrollable-content .el-table__inner-wrapper {
  background: #000 !important;
}

.scrollable-content .el-table::before {
  background-color: transparent !important;
}

.scrollable-content .el-table--border .el-table__cell {
  border: none !important;
}

.scrollable-content .el-table--border::after,
.scrollable-content .el-table--group::after,
.scrollable-content .el-table::before {
  background-color: transparent !important;
}

.trolly-label {
  background: #ff6600;
}

.trolly-content {
  color: #ffff00;
}

.duplicate-error-dialog {
  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    background-color: #ff6b35;
    border-radius: 8px;
  }
}

.duplicate-error-content {
  background-color: #ff6b35;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  border-radius: 8px;

  .el-dialog {
    margin-top: 0px !important;
    padding: 0;
    border-radius: 0;
    max-width: 800px !important;
    width: 800px !important;
  }
}

.duplicate-message {
  font-size: 28px;
  font-weight: bold;
  color: #000;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 40px;
  word-break: break-word;
}

.duplicate-error-content .close-button {
  width: 130px;
  height: 130px;
  border-radius: 50% !important;
  background: linear-gradient(145deg, #cc0000 0%, #990000 100%);
  border: 12px solid #c0c0c0;
  color: #c0c0c0;
  font-size: 76px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), inset 0 -5px 15px rgba(0, 0, 0, 0.3);
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.5), -2px -2px 4px rgba(255, 255, 255, 0.1);
  font-family: Arial, sans-serif;
  line-height: 1;
}

.duplicate-error-content .close-button:hover {
  background: linear-gradient(145deg, #dd0000 0%, #aa0000 100%);
}

.duplicate-error-content .close-button:active {
  background: linear-gradient(145deg, #bb0000 0%, #880000 100%);
  transform: translateY(2px);
}

.delete-confirm-dialog-area {
  .el-dialog {
    margin-top: 0px !important;
    padding: 0;
    border-radius: 0;
    max-width: 800px !important;
    width: 800px !important;
  }

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }

  .delete-confirmation-content {
    border: 5px solid #000;
    font-family: sans-serif;
    background: #ffffff;
  }

  .delete-dialog-header {
    background: #ffffff;
    padding: 20px;
    border-bottom: 3px solid #000;
  }

  .delete-header-text {
    color: #000;
    font-size: 48px;
    font-weight: bold;
    text-align: left;
  }

  .delete-dialog-body {
    padding: 0px 40px;
    background: #ffffff;
  }

  .delete-question {
    font-size: 36px;
    font-weight: normal;
    color: #000;
    text-align: left;
  }

  .delete-items-list {
    display: flex;
    flex-direction: column;
    height: 240px;
    overflow-y: auto;
    padding: 5px;
    background: #f5f5f5;
    border: 2px solid #000;
  }

  .delete-item {
    font-size: 32px;
    font-weight: normal;
    color: #000;
    padding: 8px 0;
    text-align: left;
  }

  .delete-dialog-buttons {
    display: flex;
    justify-content: center;
    gap: 30px;
    padding: 10px 40px;
    background: #ffffff;
  }

  .delete-dialog-btn {
    min-width: 200px;
    padding: 15px 40px;
    font-size: 36px;
    font-weight: bold;
    cursor: pointer;
    font-family: sans-serif;
    border: 3px solid #000;
    border-radius: 0;
  }

  .confirm-delete-btn {
    background: #ff4444;
    color: #fff;
    border-top: 2px solid white;
    border-left: 2px solid white;
    border-right: 2px solid black;
    border-bottom: 2px solid black;
  }

  .cancel-delete-btn {
    background: #d3d3d3;
    color: #000;
    border-top: 2px solid white;
    border-left: 2px solid white;
    border-right: 2px solid black;
    border-bottom: 2px solid black;
  }

  .cancel-delete-btn:hover {
    background: #b0b0b0;
  }

  .delete-dialog-btn:active {
    transform: translateY(2px);
  }
}
</style>
